import type { ProfileId } from '../../services/destructionProfiles';

export type CottagePieceKind = 'foundation' | 'floor' | 'wall' | 'door' | 'window' | 'roof' | 'beam' | 'chimney' | 'membrane';
export type CottageLayerId = 'foundation' | 'floor-frame' | 'exterior-brick' | 'interior-plaster' | 'opening' | 'glazing' | 'roof-frame' | 'roof-cover' | 'chimney' | 'cavity';
export type CottagePhysicalPolicy = 'structural' | 'destructible-skin' | 'absorptive';

export interface CottagePieceSpec {
  id: string;
  kind: CottagePieceKind;
  material: ProfileId;
  label: string;
  section: string;
  layer: CottageLayerId;
  physical: CottagePhysicalPolicy;
  visible: boolean;
  position: [number, number, number];
  half: [number, number, number];
  rotation: [number, number, number, number];
  supports: string[];
  quantity: { volume: number; mass: number };
}

export const COTTAGE_SCHEMA_VERSION = 1;
const IDENTITY: [number, number, number, number] = [0, 0, 0, 1];
const ROOF_SLOPE = 0.38;
const ROOF_LEFT: [number, number, number, number] = [0, 0, Math.sin(ROOF_SLOPE / 2), Math.cos(ROOF_SLOPE / 2)];
const ROOF_RIGHT: [number, number, number, number] = [0, 0, -Math.sin(ROOF_SLOPE / 2), Math.cos(ROOF_SLOPE / 2)];
const DENSITY: Record<ProfileId, number> = { concrete: 2300, brick: 1800, wood: 600, stone: 2700, glass: 2500, plaster: 1200 };

function piece(id: string, kind: CottagePieceKind, material: ProfileId, label: string, section: string, layer: CottageLayerId, physical: CottagePhysicalPolicy, position: [number, number, number], half: [number, number, number], supports: string[] = [], rotation: [number, number, number, number] = IDENTITY, visible = true): CottagePieceSpec {
  const volume = 8 * half[0] * half[1] * half[2];
  return Object.freeze({
    id,
    kind,
    material,
    label,
    section,
    layer,
    physical,
    visible,
    position: Object.freeze([...position]) as [number, number, number],
    half: Object.freeze([...half]) as [number, number, number],
    rotation: Object.freeze([...rotation]) as [number, number, number, number],
    supports: Object.freeze([...supports]) as string[],
    quantity: Object.freeze({ volume, mass: volume * DENSITY[material] }),
  });
}

const foundationIds = ['foundation-nw', 'foundation-ne', 'foundation-sw', 'foundation-se'];

/** Complete authored reference cottage. Dimensions are metres; floor y=0. */
function buildCottageDefinition(): CottagePieceSpec[] {
  const result: CottagePieceSpec[] = [
    piece('foundation-nw', 'foundation', 'stone', 'North-west stone pier', 'foundation', 'foundation', 'structural', [-3.9, .25, -2.45], [.5, .25, .5]),
    piece('foundation-ne', 'foundation', 'stone', 'North-east stone pier', 'foundation', 'foundation', 'structural', [3.9, .25, -2.45], [.5, .25, .5]),
    piece('foundation-sw', 'foundation', 'stone', 'South-west stone pier', 'foundation', 'foundation', 'structural', [-3.9, .25, 2.45], [.5, .25, .5]),
    piece('foundation-se', 'foundation', 'stone', 'South-east stone pier', 'foundation', 'foundation', 'structural', [3.9, .25, 2.45], [.5, .25, .5]),
    piece('floor-beam-front', 'beam', 'wood', 'Front floor beam', 'floor', 'floor-frame', 'structural', [0, .68, -2.45], [3.6, .16, .16], foundationIds),
    piece('floor-beam-rear', 'beam', 'wood', 'Rear floor beam', 'floor', 'floor-frame', 'structural', [0, .68, 2.45], [3.6, .16, .16], foundationIds),
  ];
  for (let i = 0; i < 6; i += 1) result.push(piece(`floor-joist-${i + 1}`, 'floor', 'wood', `Floor joist ${i + 1}`, 'floor', 'floor-frame', 'structural', [-3 + i * 1.2, .68, 0], [.14, .16, 2.1], foundationIds));

  const frame = [
    ['post-front-west', [-4.15, 2.15, -2.8] as [number, number, number], [.18, 1.5, .18] as [number, number, number]],
    ['post-front-door-west', [-1.18, 2.15, -2.8] as [number, number, number], [.15, 1.5, .15] as [number, number, number]],
    ['post-front-door-east', [1.18, 2.15, -2.8] as [number, number, number], [.15, 1.5, .15] as [number, number, number]],
    ['post-front-east', [4.15, 2.15, -2.8] as [number, number, number], [.18, 1.5, .18] as [number, number, number]],
    ['post-rear-west', [-4.15, 2.15, 2.8] as [number, number, number], [.18, 1.5, .18] as [number, number, number]],
    ['post-rear-east', [4.15, 2.15, 2.8] as [number, number, number], [.18, 1.5, .18] as [number, number, number]],
    ['post-side-left', [-4.15, 2.15, 0] as [number, number, number], [.18, 1.5, .18] as [number, number, number]],
    ['post-side-right', [4.15, 2.15, 0] as [number, number, number], [.18, 1.5, .18] as [number, number, number]],
  ] as const;
  for (const [id, position, half] of frame) result.push(piece(id, 'beam', 'wood', id.replaceAll('-', ' '), 'frame', 'floor-frame', 'structural', position, half, foundationIds));
  result.push(
    piece('plate-front', 'beam', 'wood', 'Front top plate', 'frame', 'floor-frame', 'structural', [0, 3.62, -2.8], [4.15, .15, .15], foundationIds),
    piece('plate-rear', 'beam', 'wood', 'Rear top plate', 'frame', 'floor-frame', 'structural', [0, 3.62, 2.8], [4.15, .15, .15], foundationIds),
    piece('plate-left', 'beam', 'wood', 'Left top plate', 'frame', 'floor-frame', 'structural', [-4.15, 3.62, 0], [.15, .15, 2.65], foundationIds),
    piece('plate-right', 'beam', 'wood', 'Right top plate', 'frame', 'floor-frame', 'structural', [4.15, 3.62, 0], [.15, .15, 2.65], foundationIds),
    piece('door-header', 'beam', 'concrete', 'Concrete door header', 'front opening', 'opening', 'structural', [0, 3.28, -2.8], [1.2, .2, .2], ['post-front-door-west', 'post-front-door-east']),
  );

  const brickY = [1.05, 1.82, 2.59, 3.3];
  for (let row = 0; row < brickY.length; row += 1) {
    const frontCourse = row % 2 === 0 ? [-3.15, -2.15, 2.15, 3.15] : [-3.4, -2.4, 1.9, 2.9];
    for (let column = 0; column < frontCourse.length; column += 1) result.push(piece(`brick-front-${row + 1}-${column + 1}`, 'wall', 'brick', 'Running-bond front masonry cell', 'front exterior', 'exterior-brick', 'destructible-skin', [frontCourse[column], brickY[row], -2.92], [.47, .34, .12], ['plate-front', 'post-front-west', 'post-front-east']));
  }
  for (let row = 0; row < 2; row += 1) {
    const sideCourse = row % 2 === 0 ? [-2.1, -1.1, 1.1, 2.1] : [-2.35, -1.35, .85, 1.85];
    for (let column = 0; column < sideCourse.length; column += 1) result.push(piece(`brick-right-${row + 1}-${column + 1}`, 'wall', 'brick', 'Running-bond side masonry cell', 'right exterior', 'exterior-brick', 'destructible-skin', [3.96, brickY[row + 1], sideCourse[column]], [.12, .34, .47], ['plate-right', 'post-side-right']));
  }

  for (let i = 0; i < 4; i += 1) {
    const x = i < 2 ? -3.1 + i * .75 : 2.35 + (i - 2) * .75;
    result.push(piece(`plaster-front-${i + 1}`, 'wall', 'plaster', 'Interior plaster panel', 'front interior', 'interior-plaster', 'destructible-skin', [x, 1.45 + (i % 2) * 1.2, -2.7], [.3, .52, .06], ['plate-front', 'post-front-door-west', 'post-front-door-east']));
    result.push(piece(`plaster-right-${i + 1}`, 'wall', 'plaster', 'Side interior plaster panel', 'right interior', 'interior-plaster', 'destructible-skin', [3.82, 1.45 + (i % 2) * 1.2, -1.65 + i * 1.1], [.06, .52, .42], ['plate-right', 'post-side-right']));
  }
  result.push(
    piece('door-leaf', 'door', 'wood', 'Wood door leaf', 'front opening', 'opening', 'structural', [0, 1.75, -3.04], [.88, 1.42, .08], ['door-header']),
    piece('door-jamb-west', 'beam', 'wood', 'West door jamb', 'front opening', 'opening', 'structural', [-1.05, 1.72, -3.02], [.12, 1.45, .1], ['door-header']),
    piece('door-jamb-east', 'beam', 'wood', 'East door jamb', 'front opening', 'opening', 'structural', [1.05, 1.72, -3.02], [.12, 1.45, .1], ['door-header']),
    piece('window-frame-top', 'beam', 'wood', 'Window top frame', 'right opening', 'glazing', 'structural', [4.05, 2.62, 0], [.13, .1, .72], ['post-side-right']),
    piece('window-frame-bottom', 'beam', 'wood', 'Window sill', 'right opening', 'glazing', 'structural', [4.05, .78, 0], [.13, .1, .72], ['post-side-right']),
    piece('window-frame-front', 'beam', 'wood', 'Window front jamb', 'right opening', 'glazing', 'structural', [4.05, 1.7, -.62], [.13, .82, .1], ['post-side-right']),
    piece('window-frame-rear', 'beam', 'wood', 'Window rear jamb', 'right opening', 'glazing', 'structural', [4.05, 1.7, .62], [.13, .82, .1], ['post-side-right']),
  );
  for (let row = 0; row < 2; row += 1) for (const z of [-.32, .32]) result.push(piece(`glass-pane-${row + 1}-${z < 0 ? 'f' : 'r'}`, 'window', 'glass', 'Glass window pane', 'right opening', 'glazing', 'destructible-skin', [4.06, 1.3 + row * .8, z], [.04, .34, .25], ['window-frame-top', 'window-frame-bottom']));

  const rafterZ = [-2.4, -.8, .8, 2.4];
  for (let i = 0; i < rafterZ.length; i += 1) {
    result.push(
      piece(`rafter-${i + 1}-left`, 'beam', 'wood', `Left pitched rafter ${i + 1}`, 'roof frame', 'roof-frame', 'structural', [-2.05, 4.52, rafterZ[i]], [2.22, .1, .11], ['plate-left', 'roof-ridge'], ROOF_LEFT),
      piece(`rafter-${i + 1}-right`, 'beam', 'wood', `Right pitched rafter ${i + 1}`, 'roof frame', 'roof-frame', 'structural', [2.05, 4.52, rafterZ[i]], [2.22, .1, .11], ['plate-right', 'roof-ridge'], ROOF_RIGHT),
    );
  }
  result.push(
    piece('roof-ridge', 'beam', 'wood', 'Roof ridge', 'roof frame', 'roof-frame', 'structural', [0, 5.35, 0], [.16, .16, 2.7], ['plate-front', 'plate-rear']),
    piece('ceiling-tie-front', 'beam', 'wood', 'Front ceiling tie', 'roof frame', 'roof-frame', 'structural', [0, 3.9, -2.35], [3.8, .12, .12], ['plate-front', 'plate-rear']),
    piece('ceiling-tie-rear', 'beam', 'wood', 'Rear ceiling tie', 'roof frame', 'roof-frame', 'structural', [0, 3.9, 2.35], [3.8, .12, .12], ['plate-front', 'plate-rear']),
  );
  for (let row = 0; row < 3; row += 1) {
    result.push(piece(`roof-cover-left-${row + 1}`, 'roof', 'wood', 'Left roof deck panel', 'roof cover', 'roof-cover', 'destructible-skin', [-2.05, 4.55, -1.8 + row * 1.8], [2.22, .11, .82], [`rafter-${row + 1}-left`, `rafter-${row + 2}-left`], ROOF_LEFT));
    result.push(piece(`roof-cover-right-${row + 1}`, 'roof', 'wood', 'Right roof deck panel', 'roof cover', 'roof-cover', 'destructible-skin', [2.05, 4.55, -1.8 + row * 1.8], [2.22, .11, .82], [`rafter-${row + 1}-right`, `rafter-${row + 2}-right`], ROOF_RIGHT));
  }
  result.push(
    piece('chimney-base', 'chimney', 'stone', 'Stone chimney base', 'chimney', 'chimney', 'structural', [1.25, 5.65, .75], [.42, .42, .42], ['roof-ridge']),
    piece('chimney-cap', 'chimney', 'concrete', 'Concrete chimney cap', 'chimney', 'chimney', 'structural', [1.25, 6.22, .75], [.48, .18, .48], ['chimney-base']),
    piece('cavity-weather-membrane', 'membrane', 'plaster', 'Weather membrane (logical)', 'wall cavity', 'cavity', 'absorptive', [0, 2, 0], [4, 1.4, .01], ['plate-front'], IDENTITY, false),
    piece('cavity-insulation', 'membrane', 'plaster', 'Insulation (logical)', 'wall cavity', 'cavity', 'absorptive', [0, 2, 0], [3.8, 1.3, .01], ['plate-front'], IDENTITY, false),
  );
  return result;
}

export const COTTAGE_PIECES: readonly CottagePieceSpec[] = Object.freeze(buildCottageDefinition());

export function validateCottageSpec(pieces: readonly CottagePieceSpec[] = COTTAGE_PIECES): void {
  const ids = new Set<string>();
  for (const node of pieces) {
    if (ids.has(node.id)) throw new Error(`duplicate cottage node id ${node.id}`);
    ids.add(node.id);
    if (!node.label || !node.section || !node.layer || !node.physical) throw new Error(`incomplete cottage node ${node.id}`);
    if (!Array.isArray(node.position) || !Array.isArray(node.half) || !Array.isArray(node.rotation) || !node.position.every(Number.isFinite) || !node.half.every((value) => Number.isFinite(value) && value > 0) || !node.rotation.every(Number.isFinite)) throw new Error(`invalid transform for cottage node ${node.id}`);
    if (!Number.isFinite(node.quantity.volume) || node.quantity.volume <= 0 || !Number.isFinite(node.quantity.mass) || node.quantity.mass <= 0) throw new Error(`invalid quantity for cottage node ${node.id}`);
  }
  for (const node of pieces) {
    if (new Set(node.supports).size !== node.supports.length) throw new Error(`${node.id} contains duplicate supports`);
    for (const support of node.supports) {
      if (support === node.id) throw new Error(`${node.id} cannot support itself`);
      if (!ids.has(support)) throw new Error(`${node.id} references missing support ${support}`);
    }
  }

  const byId = new Map(pieces.map((node) => [node.id, node]));
  const visiting = new Set<string>();
  const visited = new Set<string>();
  const visit = (node: CottagePieceSpec) => {
    if (visited.has(node.id)) return;
    if (visiting.has(node.id)) throw new Error(`cyclic support path at ${node.id}`);
    visiting.add(node.id);
    for (const support of node.supports) visit(byId.get(support)!);
    visiting.delete(node.id);
    visited.add(node.id);
  };
  for (const node of pieces) visit(node);
}

export function createCottageSpec(): CottagePieceSpec[] {
  validateCottageSpec();
  return COTTAGE_PIECES.map((p) => ({ ...p, position: [...p.position] as [number, number, number], half: [...p.half] as [number, number, number], rotation: [...p.rotation] as [number, number, number, number], supports: [...p.supports], quantity: { ...p.quantity } }));
}
